import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { getNodeSupport } from '@atlassian/ai-model-io/utils/get-node-support';
import { FIRST_NUDGE_CHECK_DURATION, NUDGE_AFTER_DOC_CHANGE_DURATION, NUDGE_DURATION } from './constants';
import { canNudgeFullPageImproveWriting } from './request';

/**
 * Used `nudger` noun to name the interface as it is the one who nudges.
 * `nudger` dictionary meaning: someone who nudges
 */

export var FullPageImproveWritingNudger = /*#__PURE__*/function () {
  function FullPageImproveWritingNudger(aiSmartButtonOptions, nudgerOptions) {
    _classCallCheck(this, FullPageImproveWritingNudger);
    _defineProperty(this, "active", false);
    _defineProperty(this, "lastDocChangeTime", 0);
    this.aiSmartButtonOptions = aiSmartButtonOptions;
    this.nudgerOptions = nudgerOptions;
    this.lastDocChangeTime = Date.now();
  }
  return _createClass(FullPageImproveWritingNudger, [{
    key: "setLastDocChangeTime",
    value: function setLastDocChangeTime(time) {
      this.lastDocChangeTime = time;
    }
  }, {
    key: "isActive",
    value: function isActive() {
      return this.active;
    }
  }, {
    key: "checkForNudge",
    value: function () {
      var _checkForNudge = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _this$nudgerOptions$a;
        var currentTimeStamp, hasUnresolvedComments, supportedNodes, _yield$canNudgeFullPa, canNudge, nudgeMetrics, nudge;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (!(!this.active || !this.aiSmartButtonOptions || !this.nudgerOptions)) {
                _context.next = 2;
                break;
              }
              return _context.abrupt("return");
            case 2:
              /**
               * Wait NUDGE_AFTER_DOC_CHANGE_DURATION milliseconds after the last document change before nudging.
               * When user is contantly typing and updating the document, we don't want to nudge the user too frequently.
               * We want to wait for a pause in typing before nudging.
               * In future we will employ a more sophisticated algorithm to determine when to nudge.
               */
              currentTimeStamp = Date.now();
              if (!(currentTimeStamp < this.lastDocChangeTime + NUDGE_AFTER_DOC_CHANGE_DURATION)) {
                _context.next = 6;
                break;
              }
              /**
               * Check again after the remaining time, if user types again,
               * 	will keep waiting till user stops typing for NUDGE_AFTER_DOC_CHANGE_DURATION
               */
              this.next(this.lastDocChangeTime + NUDGE_AFTER_DOC_CHANGE_DURATION - currentTimeStamp);
              return _context.abrupt("return");
            case 6:
              /**
               * If there are any unresolved comments in the document, then we don't want to nudge.
               */
              hasUnresolvedComments = (_this$nudgerOptions$a = this.nudgerOptions.api) === null || _this$nudgerOptions$a === void 0 || (_this$nudgerOptions$a = _this$nudgerOptions$a.annotation) === null || _this$nudgerOptions$a === void 0 ? void 0 : _this$nudgerOptions$a.actions.hasAnyUnResolvedAnnotationInPage(this.nudgerOptions.view.state);
              if (!hasUnresolvedComments) {
                _context.next = 10;
                break;
              }
              // Author can delete comments, so we will check again after NUDGE_DURATION (2 minutes)
              this.next();
              return _context.abrupt("return");
            case 10:
              // Check if the document has only nodes supported by MD/MD+.
              supportedNodes = new Set(Object.entries(getNodeSupport()).filter(function (_ref) {
                var _ref2 = _slicedToArray(_ref, 2),
                  value = _ref2[1];
                return value === 'full' || value === 'custom';
              }).map(function (_ref3) {
                var _ref4 = _slicedToArray(_ref3, 1),
                  key = _ref4[0];
                return key;
              }));
              supportedNodes.add('doc');
              if (this.hasOnlySupportedNodes(this.nudgerOptions.view.state.doc, supportedNodes)) {
                _context.next = 15;
                break;
              }
              this.next();
              return _context.abrupt("return");
            case 15:
              _context.next = 17;
              return canNudgeFullPageImproveWriting({
                proactiveAIApiUrl: this.nudgerOptions.proactiveAIApiUrl,
                product: this.nudgerOptions.product,
                context: {
                  view: this.nudgerOptions.view,
                  getMentionNameDetails: this.nudgerOptions.getMentionNameDetails,
                  locale: this.nudgerOptions.locale
                }
              });
            case 17:
              _yield$canNudgeFullPa = _context.sent;
              canNudge = _yield$canNudgeFullPa.canNudge;
              nudgeMetrics = _yield$canNudgeFullPa.nudgeMetrics;
              this.nudgerOptions.onCollectMetrics(nudgeMetrics);
              if (canNudge) {
                nudge = this.nudgerOptions.getNudge(nudgeMetrics);
                this.aiSmartButtonOptions.actions.setNudges([nudge]);
                // Once we add a nudge, need to stop, because we nudge only once per editing session.
                this.stop();
              } else {
                this.next();
              }
            case 22:
            case "end":
              return _context.stop();
          }
        }, _callee, this);
      }));
      function checkForNudge() {
        return _checkForNudge.apply(this, arguments);
      }
      return checkForNudge;
    }()
  }, {
    key: "next",
    value: function next() {
      var duration = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : NUDGE_DURATION;
      if (this.timerId) {
        clearTimeout(this.timerId);
      }
      this.timerId = setTimeout(this.checkForNudge.bind(this), duration);
    }
  }, {
    key: "start",
    value: function start() {
      if (!this.active) {
        this.active = true;
        this.next(FIRST_NUDGE_CHECK_DURATION);
      }
    }
  }, {
    key: "stop",
    value: function stop() {
      this.active = false;
      clearTimeout(this.timerId);
      this.timerId = undefined;
    }
  }, {
    key: "hasOnlySupportedNodes",
    value: function hasOnlySupportedNodes(node, supportedNodes) {
      var isNodeTypeSupported = supportedNodes.has(node.type.name);
      if (!isNodeTypeSupported && node.attrs) {
        var isValid = false;
        switch (node.type.name) {
          case 'table':
            var hasCorrectLayout = node.attrs.layout === 'default';
            isValid = hasCorrectLayout && this.areRemainingAttrsFalsey(node.attrs, ['layout', 'localId', 'width']);
            break;
          case 'tableHeader':
          case 'tableRow':
          case 'tableCell':
            var hasValidColspan = node.attrs.colspan === undefined || node.attrs.colspan === 1;
            var hasValidRowspan = node.attrs.rowspan === undefined || node.attrs.rowspan === 1;
            isValid = hasValidColspan && hasValidRowspan && this.areRemainingAttrsFalsey(node.attrs, ['colspan', 'rowspan']);
            break;
          case 'mediaSingle':
            var hasMediaCorrectLayout = node.attrs.layout === 'center';
            var hasCorrectWidthType = !node.attrs.widthType || node.attrs.widthType === 'pixel';
            isValid = hasMediaCorrectLayout && hasCorrectWidthType && this.areRemainingAttrsFalsey(node.attrs, ['layout', 'widthType', 'width']);
            break;
          default:
            return false;
          // Node type not supported and not a special case.
        }
        if (!isValid) {
          return false;
        }
      }
      for (var i = 0; i < node.childCount; i++) {
        if (!this.hasOnlySupportedNodes(node.child(i), supportedNodes)) {
          return false;
        }
      }
      return true;
    }

    /**
     * Checks if all attributes not in the ignore list have falsey values
     */
  }, {
    key: "areRemainingAttrsFalsey",
    value: function areRemainingAttrsFalsey(attrs, ignoreAttrs) {
      var remainingAttrs = Object.keys(attrs).filter(function (attr) {
        return !ignoreAttrs.includes(attr);
      });
      return remainingAttrs.every(function (attr) {
        return !attrs[attr];
      });
    }
  }]);
}();